import { useState } from 'react';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, API_BASE } from '@/lib/api';

/**
 * Provider-agnostic image uploader.
 *
 * - Sends each picked file to `POST /api/uploads/product-image`.
 * - Stores the returned `path` in the parent state (NOT a base64 blob nor a
 *   full URL) so the same product is portable across providers later.
 * - Renders previews via `GET /api/uploads/{path}` which proxies the storage
 *   provider — Emergent today, Cloudinary/S3 tomorrow.
 */
export const resolveImageUrl = (pathOrUrl) => {
  if (!pathOrUrl) return '';
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  if (pathOrUrl.startsWith('data:')) return pathOrUrl;
  if (pathOrUrl.startsWith('/api/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}${pathOrUrl}`;
  return `${API_BASE}/uploads/${pathOrUrl}`;
};

export const ImageUploader = ({ images, onChange, max = 8, testId = 'image-uploader' }) => {
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > max) {
      toast.error(`Máximo ${max} imágenes`);
      return;
    }
    setBusy(true);
    const uploaded = [];
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} supera 5MB`); continue; }
        const form = new FormData();
        form.append('file', f);
        const { data } = await api.post('/uploads/product-image', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(data.path);
      }
      onChange([...images, ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} imagen(es) subida(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {images.map((p, i) => (
          <div key={`${p}-${i}`} className="relative aspect-square border border-slate-200 bg-slate-50 group">
            <img
              src={resolveImageUrl(p)}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${testId}-remove-${i}`}
              className="absolute top-1 right-1 h-6 w-6 bg-white/95 border border-slate-300 hover:border-rose-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              aria-label="Eliminar"
            >
              <X className="h-3.5 w-3.5 text-rose-600" />
            </button>
            {i === 0 && (
              <div className="absolute bottom-1 left-1 text-[9px] font-bold uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5">Principal</div>
            )}
          </div>
        ))}

        {images.length < max && (
          <label
            data-testid={`${testId}-input-label`}
            className="aspect-square border-2 border-dashed border-slate-300 hover:border-orange-500 hover:bg-orange-50 cursor-pointer flex flex-col items-center justify-center gap-1 text-slate-500 transition-colors"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Subir</span>
              </>
            )}
            <input
              data-testid={`${testId}-input`}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={onPick}
              disabled={busy}
              className="hidden"
            />
          </label>
        )}
      </div>
      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
        <ImageIcon className="h-3 w-3" />
        JPG / PNG / WEBP — hasta 5MB · {images.length}/{max} imágenes
      </div>
    </div>
  );
};
